import React, { useState, useEffect, useMemo } from 'react';
import './styles/ParsedResultsPopup.css';

function ParsedResultsPopup({ parsedResults, onClose, currentPage, pageSize, onPageChange, predefinedSearch }) {
    const [filter, setFilter] = useState('');
    const [filteredResults, setFilteredResults] = useState([]);
    useEffect(() => {
        const applyFilter = () => {
            console.log("Filtering with:", filter);  // Debug: Log current filter
            if (!filter) {
                setFilteredResults(parsedResults);
                console.log("No filter set, using parsed results:", parsedResults);  // Debug: Log when no filter is applied
                return;
            }
            const filterRegex = new RegExp(filter, 'i');
            if (predefinedSearch === 'EPD Failure Parse') {
                const newResults = parsedResults.filter(line => filterRegex.test(line.rawLine || ''));
                setFilteredResults(newResults);
                console.log("Filtered Results for EPD Failure Parse:", newResults);  // Debug: Log filtered results
            } else {
                // Handle Default Parse
                const newResults = Object.entries(parsedResults).reduce((acc, [keyword, files]) => {
                    const filteredFiles = Object.entries(files).reduce((fileAcc, [file, lines]) => {
                        const filteredLines = lines.filter(line => filterRegex.test(line));
                        if (filteredLines.length > 0) {
                            fileAcc[file] = filteredLines;
                        }
                        return fileAcc;
                    }, {});
                    if (Object.keys(filteredFiles).length > 0) {
                        acc[keyword] = filteredFiles;
                    }
                    return acc;
                }, {});
                setFilteredResults(newResults);
                console.log("Filtered Results for Default Parse:", newResults);  // Debug: Log filtered results
            }
        };
        applyFilter();
    }, [filter, parsedResults, predefinedSearch]);


    const getPagedMatches = useMemo(() => {
        if (predefinedSearch === 'EPD Failure Parse') {
            const start = currentPage * pageSize;
            const end = start + pageSize;
            return filteredResults.slice(start, end);
        } else {
            return filteredResults; // Return all results for Default Parse, as we will render them differently
        }
    }, [currentPage, pageSize, filteredResults, predefinedSearch]);

    const renderPagination = (matchesLength) => {
        if (predefinedSearch !== 'EPD Failure Parse') return null;
        const totalPages = Math.ceil(matchesLength / pageSize);
        if (totalPages <= 1) return null;

        const pages = [];
        if (currentPage > 0) {
            pages.push(
                <button key="prev" onClick={() => onPageChange(currentPage - 1)}>
                    Prev
                </button>
            );
        }
        for (let i = 0; i < totalPages; i++) {
            if (i === currentPage || (i >= currentPage - 4 && i <= currentPage + 4) || i === 0 || i === totalPages - 1) {
                pages.push(
                    <button
                        key={i}
                        onClick={() => onPageChange(i)}
                        className={currentPage === i ? 'active' : ''}
                    >
                        {i + 1}
                    </button>
                );
            } else if (i === currentPage - 5 || i === currentPage + 5) {
                pages.push(<span key={`ellipsis-${i}`}>...</span>);
            }
        }
        if (currentPage < totalPages - 1) {
            pages.push(
                <button key="next" onClick={() => onPageChange(currentPage + 1)}>
                    Next
                </button>
            );
        }
        return <div className="pagination-controls">{pages}</div>;
    };

    const highlightText = (text, filter) => {
        if (!filter) return text;
        const parts = text.split(new RegExp(`(${filter})`, 'gi'));
        return parts.map((part, index) =>
            part.toLowerCase() === filter.toLowerCase() ? <span key={index} className="highlight">{part}</span> : part
        );
    };

    const downloadCSV = () => {
        if (predefinedSearch === 'EPD Failure Parse') {
            const headers = ['Label ID', 'Label Code', 'Label MAC Address', 'Station ID', 'Store Code'];
            const rows = filteredResults.map(result => [
                result.id,
                result.code,
                result.macAddress,
                result.stationId,
                result.storeCode
            ]);

            let csvContent = "data:text/csv;charset=utf-8,"
                + headers.join(",") + "\n"
                + rows.map(e => e.join(",")).join("\n");

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", "parsed_results.csv");
            document.body.appendChild(link); // Required for FF

            link.click();
            document.body.removeChild(link);
        } else {
            // Handle Default Parse CSV download
            const headers = ['Keyword', 'File', 'Matched Lines'];
            const rows = Object.entries(filteredResults).flatMap(([keyword, files]) =>
                Object.entries(files).flatMap(([file, lines]) =>
                    lines.map(line => [keyword, file, line])
                )
            );

            let csvContent = "data:text/csv;charset=utf-8,"
                + headers.join(",") + "\n"
                + rows.map(e => e.join(",")).join("\n");

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", "parsed_results.csv");
            document.body.appendChild(link); // Required for FF

            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <div className="popup-overlay">
            <div className="popup-content">
                <div className="popup-header">
                    <div className="pagination-header">
                        <label htmlFor="page-size-select">Results per page: </label>
                        <select id="page-size-select" value={pageSize} onChange={(e) => onPageChange(0, Number(e.target.value))}>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={150}>150</option>
                            <option value={200}>200</option>
                        </select>
                    </div>
                    <button className="close-button" onClick={onClose}>Close</button>
                </div>
                <div className="filter-header">
                    <label htmlFor="filter-input">Filter: </label>
                    <input
                        id="filter-input"
                        type="text"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Type to filter results"
                    />
                    <button onClick={downloadCSV} className="download-csv-button">Download CSV</button>
                </div>
                <div className="results-content">
                    {predefinedSearch === 'EPD Failure Parse' ? (
                        <table className="results-table">
                            <thead>
                                <tr>
                                    <th>EPD Display Failure Results</th>
                                </tr>
                            </thead>
                            <tbody>
                                {getPagedMatches.map((line, index) => (
                                    <tr key={index}>
                                        <td>{highlightText(line.rawLine || line.line, filter)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        Object.entries(filteredResults).map(([keyword, files]) => (
                            <div key={keyword} className="keyword-results">
                                <h4>{keyword}</h4>
                                {Object.entries(files).map(([file, lines]) => (
                                    <details key={file}>
                                        <summary>{file}</summary>
                                        <pre>
                                            {lines.map((line, index) => (
                                                <div key={index}>{highlightText(line, filter)}</div>
                                            ))}
                                        </pre>
                                    </details>
                                ))}
                            </div>
                        ))
                    )}
                    {predefinedSearch === 'EPD Failure Parse' && renderPagination(filteredResults.length)}
                </div>
            </div>
        </div>
    );
}

export default ParsedResultsPopup;
